<template>
    <div>
        <ul class="uk-subnav uk-subnav-line">
            <li :class="{'uk-active': !state.status}"><a href @click.prevent="filter_by_field('', 'status')">All</a></li>
            <li
                v-for="status in available_project_status"
                :key="status.id"
                :class="{'uk-active': (state.status === (status.code_constant||status.id)) }"
            >
                <a href @click.prevent="filter_by_field(status.code_constant||status.id, 'status')">{{ status.name }} ({{ status.prospective_usage_count || 0 }})</a>
            </li>
        </ul>

        <data-table
            id="prospective-projects"
            :columns="visible_columns"
            :rows="filtered_rows"
            :search="state.search"
            :total="state.total"
            :found="state.found"
            :current-page="state.page"
            :total-pages="nb_pages"
            :searchable="false"
            table-class="lam-records-list"
            :row-class-provider="row_classes"
            empty-title="No projects found"
            :export-entity="entity"
            :export-query="state"
            :export-fields="export_fields"
            @sort="sort_by"
            @search="search"
            @page="goto_page"
            @click="record_edit(entity, $event.id)"
        >
            <template v-slot:controls>
                <div>
                    <base-button
                        flavor="link"
                        @click.prevent="toggle_filters(true)"
                    >
                        <span v-html="applied_filters"></span>
                    </base-button>
                    <base-button
                        v-if="!!filter_description"
                        flavor="link"
                        @click.prevent="clear_filters"
                        class="uk-margin-left"
                    >
                        <open-icon glyph="close" />
                    </base-button>
                    <base-button
                        v-if="!!templates.length"
                        flavor="link"
                        @click.native="picking_template=!picking_template"
                    >
                        <open-icon glyph="list-ul" />
                    </base-button>
                </div>
            </template>
            <template v-slot:mid-controls>
                <div>
                    <form-select-user
                        id="champion_id_filter"
                        name="champion_id_filter"
                        :value="state.champion_id"
                        :editable="true"
                        empty-label="(All Champs)"
                        :empty-value="0"
                        :required="false"
                        @input="filter_by_field($event, 'champion_id')"
                    />
                </div>
            </template>
            <template
                v-slot:summary="{ row }"
            >
                <h6>{{ row.name }}</h6>
            </template>
            <template
                v-slot:tbody="{ row, field }"
            >
                <router-link
                    v-if="field === 'name'"
                    :to="{ name: 'prospective_project.edit', params: { id: row.id } }"
                >
                    {{ row.name }}
                    <span
                        v-if="!!row.other_teams"
                        class="uk-margin-small-left"
                    >
                        <open-icon glyph="group" title="Other Teams" />
                    </span>
                    <span
                        v-if="!row.proposal_amount"
                        class="uk-text-warning uk-margin-small-left"
                    >
                        <open-icon glyph="file-pdf-o" title="Proposal not written" />
                    </span>
                </router-link>
                <span v-else-if="field === 'sortable_number'">{{ row.sortable_number | lam.project-number }}</span>
                <span v-else-if="field.match(/computed_(prospective_fee|fee|spent)/i)">$&nbsp;{{ row[field] | nibnut.number("0,0") }}</span>
                <div v-else-if="field === 'computed_starts_at_as_string'">
                    <span
                        v-if="!row.computed_starts_at_as_string || !row.computed_ends_at_as_string"
                        class="uk-text-danger"
                    >
                        <open-icon glyph="calendar-times-o" />
                    </span>
                    <span v-else>{{ row[field] | nibnut.date("MM/YYYY") }}</span>
                </div>
                <div v-else-if="field === 'computed_last_communication_at_as_string'">
                    <span
                        v-if="!row[field]"
                        class="uk-text-danger"
                    >
                        <open-icon glyph="calendar-times-o" />
                    </span>
                    <span v-else>{{ row[field] | nibnut.date("MM/DD/YY") }}</span>
                </div>
                <div v-else-if="field === 'proposal_sent_at_as_string'">
                    <span
                        v-if="!row[field]"
                        class="uk-text-danger"
                    >
                        <open-icon glyph="calendar-times-o" />
                    </span>
                    <span v-else>{{ row[field] | nibnut.date("MM/DD/YY") }}</span>
                </div>
                <span v-else-if="field === 'days_since_received'">
                    <span v-if="row[field] >= 60">{{ (row[field] / 30) | nibnut.number("0,0") }}&nbsp;months</span>
                    <span v-else>{{ row[field] | nibnut.number("0,0") }}&nbsp;days</span>
                </span>
                <span v-else-if="field.match(/(completed|computed_starts)_at_as_string/)">{{ row[field] | nibnut.date("MM/YYYY") }}</span>
                <span v-else-if="field === 'consider_for_marketing'">
                    <small v-if="!!row[field]" class="uk-text-warning"><open-icon glyph="warning" title="This project should be considered for marketing purposes" /> Should be considered</small>
                </span>
                <div
                    v-else-if="field === 'automated_communication_log_count'"
                >
                    <button
                        v-if="(row.recent_user_note_count + row.automated_communication_log_count) > 0"
                        type="button"
                        :class="{ 'uk-button-primary-w-icon': !!row.automated_communication_log_count && !row.recent_user_note_count, 'uk-button-danger': !!row.recent_user_note_count }"
                        class="uk-button"
                        @click.prevent.stop="display_logs_for_project(row)"
                    >
                        <open-icon glyph="sticky-note" />
                    </button>
                </div>
                <span v-else>{{ row[field] }}</span>
            </template>
        </data-table>

        <projects-list-filter-dialog
            :show.sync="showing_filters"
            :state="state"
            :entity="entity"
            :prospective="prospective"
            :stateUi="state_ui"
            :available-project-status="available_project_status"
            :size-unit-html="size_unit_html"
            @filter="filter_by_field"
            @pick-firm="pick_firm"
            @units-changed="units_changed"
        >
            <template v-slot:pick-template-button>
                <span v-if="!!templates.length"  class="uk-margin-left">
                    <base-button size="mini" @click.native="picking_template=!picking_template">
                        <open-icon glyph="list-ul" />
                    </base-button>
                </span>
            </template>
            <template v-slot:templates-controls>
                <templates-controls
                    :template="current_template"
                    :entity="entity"
                    category="filters"
                    :savable="template_savable"
                    :dirty="template_dirty"
                    :clone="template_to_clone"
                    @save="template_editor_save"
                    @stop-editing="template_editor_close"
                />
            </template>
        </projects-list-filter-dialog>
        <project-logs-dialog
            :project="viewing_logs_for_project || {}"
            :show.sync="viewing_logs"
        />
        <template-picker
            :template="current_template"
            :entity="entity"
            :active="picking_template"
            title="Saved Filters"
            category="filters"
            @close="picking_template=false"
            @clone="template_clone"
            @load="template_load"
        />
    </div>
</template>

<script type="text/javascript">
import { mapGetters } from "vuex"

import is_projects_list from "./IsProjectsList"

import FormSelectUser from "../../../ui/FormSelectUser"

export default {
    mixins: [is_projects_list],
    components: {
        FormSelectUser
    },
    methods: {
        row_classes (row) {
            const old = row.computed_last_communication_at_as_string ? (this.$moment().diff(this.$moment(row.computed_last_communication_at_as_string), "days") >= 30) : false
            return { "uk-text-danger": old }
        }
    },
    computed: {
        ...mapGetters(["entity_records"]),
        state_identifier () {
            return "prospective_project.list"
        },
        visible_columns () {
            const columns = {}

            columns.champion_initials = this.columns.champion_initials
            columns.name = this.columns.name
            columns.name.label = "Prospective Project Name"
            columns.client_name = this.columns.client_name
            columns.current_state = this.columns.current_state
            columns.days_since_received = this.columns.days_since_received
            columns.type_of_request_label = this.columns.type_of_request_label
            columns.computed_prospective_fee = this.columns.computed_prospective_fee
            columns.proposal_sent_at_as_string = this.columns.proposal_sent_at_as_string
            columns.computed_last_communication_at_as_string = this.columns.computed_last_communication_at_as_string
            columns.computed_starts_at_as_string = this.columns.computed_starts_at_as_string
            columns.automated_communication_log_count = this.columns.automated_communication_log_count

            return columns
        },
        available_project_status () {
            let statii = this.dictionary(this.$root.constants.dictionaries.SHELF_LIFE, "name").filter((term) => {
                if(!!term && !!term.code_constant) return true
                return false
            })
            statii = statii.concat(
                this.dictionary(this.$root.constants.dictionaries.PROJECT_STATUS, "name").filter((term) => {
                    if(term.code_constant.match(/^(chosen-pending|other)$/i)) return true
                    return false
                })
            )
            return statii
        }
    },
    data () {
        return {
            prospective: true,
            addl_export_fields: [
                "computed_prospective_fee", "insider_knowledge_label", "last_rfm_activity", "last_changed_at_as_string",
                "computed_last_communication_at_as_string", "received_by_label", "method_of_contact_label", "responded_at_as_string",
                "response_by_label", "other_teams", "fee_type", "fee_status", "revisions_count", "project_sustainability_programs_label"
            ]
        }
    }
}
</script>
